import { format } from 'date-fns-tz';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import { type IntlShape } from 'react-intl-next';

import { messages as commonMessages } from '../../common/messages';
import { type EpochRange, type PeriodRangeEdge, type TimeFrame } from '../../common/types';
import { getNowLocation } from '../schedule';

import { messages } from './messages';

const computePosition = (inner: EpochRange, outer: TimeFrame) => {
	// how much space inner item takes overall as ratio
	const itemRatio = (inner.endEpoch - inner.startEpoch) / (outer.endEpoch - outer.startEpoch);

	// how far is the inner item from the start of the outer item as ratio
	const itemPosition = (inner.startEpoch - outer.startEpoch) / (outer.endEpoch - outer.startEpoch);

	// how far is the inner item from the left as pixel
	const itemLeft = itemPosition * outer.width;

	// actual pixel of inner item
	const itemWidth = itemRatio * outer.width;

	// whether inner item is in past, present or future
	const nowLocation = getNowLocation(inner.startEpoch, inner.endEpoch, outer.nowEpoch);

	// how much of the inner item is filled with overlay (how much of is past time) as ratio (normalized between 0 and 1)
	const nowFillRatio = Math.max(
		Math.min((outer.nowEpoch - inner.startEpoch) / (inner.endEpoch - inner.startEpoch), 1),
		0,
	);

	return {
		itemRatio,
		itemLeft,
		itemPosition,
		itemWidth,
		nowFillRatio,
		nowLocation,
	};
};

const computeBorderRadius = ({
	leftRounded,
	rightRounded,
	radius,
}: {
	leftRounded: boolean;
	rightRounded: boolean;
	radius: string;
}) => {
	if (leftRounded && rightRounded) {
		return `
      border-radius: ${radius};
    `;
	} else if (!leftRounded && !rightRounded) {
		return `
      border-radius: 0;
    `;
	} else if (leftRounded) {
		return `
      border-top-left-radius: ${radius};
      border-bottom-left-radius: ${radius};
    `;
	} else if (rightRounded) {
		return `
      border-top-right-radius: ${radius};
      border-bottom-right-radius: ${radius};
    `;
	}

	throw new Error('Invalid combination of roundedLeft and roundedRight');
};

// TODO: Establish common time range format - https://ifountain.atlassian.net/browse/TARDIS-8527
/**
 * @returns string - 'From 12:18 AM to Later than the current period' | ...
 */
const formatAccessibleRange = (intlApi: IntlShape, time: PeriodRangeEdge) => {
	const start = utcToZonedTime(time.startEpoch + time.startOffset, 'UTC');
	const end = utcToZonedTime(time.endEpoch + time.endOffset, 'UTC');

	const startTime = time.isTrueStartTime
		? format(start, 'h:mm a')
		: intlApi.formatMessage(commonMessages.noStartTime);

	const endTime = time.isTrueEndTime
		? format(end, 'h:mm a')
		: intlApi.formatMessage(commonMessages.noEndTime);

	return intlApi.formatMessage(messages.timeRange, {
		start: startTime,
		end: endTime,
	});
};

export { computePosition, computeBorderRadius, formatAccessibleRange };
